import { navigationClickEvent } from 'analytics/events/studio';
import clsx from 'clsx';
import React, { useState } from 'react';
import { Col, Row, Container, SpanConfig, Space, Typography } from '@ag1/ui';
import { getSectionPaddingClasses } from 'shared/utils/getSectionPaddingClasses';
import { FaqItemV2, FaqV2, SanityKeyed } from 'types/studio/studio';
import CustomPortableText from '../PortableText/PortableText';
import { StudioBaseIconV2 } from '../StudioBaseIcon/StudioBaseIcon';
import { FaqQuestion } from './FaqQuestion/FaqQuestion';
import styles from './FaqV2.module.scss';
import { CommonSectionProps } from '../types';

export const TEXT_COL_CONFIG = { xxs: 12, l: 5, xl: 4 } satisfies SpanConfig;
export const QUESTION_COL_CONFIG_BROAD = { xxs: 12, l: 7 } satisfies SpanConfig;
export const QUESTION_COL_CONFIG_NARROW = { xxs: 12, l: 6 } satisfies SpanConfig;

const FaqBlockV2Markup = ({
  _key: key,
  _type: sectionType,
  variant,
  headline,
  subtitle,
  portableText,
  badges,
  badgesSet,
  questions,
  padding
}: SanityKeyed<FaqV2> & CommonSectionProps) => {
  const textColConfig = TEXT_COL_CONFIG;
  const questionColConfig =
    variant === 'questions_broad' ? QUESTION_COL_CONFIG_BROAD : QUESTION_COL_CONFIG_NARROW;

  const [selected, setSelected] = useState(questions?.[0]?._key ? questions[0]._key : null);
  const acordionRef = React.useRef<HTMLDivElement>(null);

  const handleToggleQuestion = ({
    itemKey,
    itemHeadline,
    toggledIndex
  }: {
    itemKey: string;
    itemHeadline: string;
    toggledIndex: number;
  }) => {
    if (selected !== itemKey) {
      navigationClickEvent({
        header: headline,
        subheader: subtitle,
        location: sectionType,
        navigationType: 'accordion',
        clickText: itemHeadline
      });
    }

    // trigger this after 300ms to allow the css transition to finish
    setTimeout(() => {
      if (acordionRef.current !== null) {
        const tabHeaderBoundingRect =
          acordionRef.current.children[toggledIndex].getBoundingClientRect();
        const nextTabItem =
          toggledIndex + 1 >= questions.length
            ? null
            : acordionRef.current.children[toggledIndex + 1];
        const viewportHeight = window.innerHeight;
        const currentScroll = window.scrollY;
        let scrollDown = 0;
        let scrollUp = 0;

        if (nextTabItem !== null) {
          const nextTabItemBoundingRect = nextTabItem.getBoundingClientRect();
          if (nextTabItemBoundingRect.bottom > viewportHeight) {
            // we scroll 48px lower to give a bit of space
            scrollDown = nextTabItemBoundingRect.bottom - viewportHeight + 48;
          }
        } else {
          const currentTabItemBodyRect =
            acordionRef.current.children[toggledIndex].getBoundingClientRect();
          if (currentTabItemBodyRect.bottom > viewportHeight) {
            scrollDown = currentTabItemBodyRect.bottom - viewportHeight;
          }
        }

        // the header is 90px so we take that into account
        if (tabHeaderBoundingRect.top < 90) {
          scrollUp = 90 - tabHeaderBoundingRect.top;
        }

        if (scrollUp > 0) {
          window.scrollTo({
            top: currentScroll - scrollUp,
            behavior: 'smooth'
          });
        }
        // the header is 90px so we take that into account
        else if (scrollDown > 0) {
          if (tabHeaderBoundingRect.top - scrollDown < 90) {
            window.scrollTo({
              top: currentScroll + tabHeaderBoundingRect.top - 90,
              behavior: 'smooth'
            });
          } else {
            window.scrollTo({
              top: currentScroll + scrollDown,
              behavior: 'smooth'
            });
          }
        }
      }
    }, 350);

    setSelected((prev) => (prev === itemKey ? null : itemKey));
  };

  return (
    <Container narrow id={key}>
      <Space
        top={{ xxs: 40, s: 80, l: 100 }}
        bottom={{ xxs: 80, l: 100 }}
        className={clsx(getSectionPaddingClasses(styles, padding))}>
        <Row align="between" className={styles.row}>
          <Col span={textColConfig} className={styles.faqCol}>
            <Typography
              variant="headlineSans"
              size="2"
              component="h2"
              className={styles.headline}
              color="black-new">
              {headline}
            </Typography>
            {subtitle && (
              <Typography
                variant="subtitle"
                size="1"
                noPadding
                component="h3"
                weight="700"
                className={styles.subtitleMobile}
                color="black-new">
                {subtitle}
              </Typography>
            )}
            {portableText && <CustomPortableText value={portableText} className={styles.text} />}
            {badges && (
              <div className={styles.badges_wrap}>
                {badgesSet?.map((item) => (
                  <StudioBaseIconV2 key={item._key} icon={item} className={styles.badge} />
                ))}
              </div>
            )}
          </Col>
          <Col span={questionColConfig} className={styles.questions_col}>
            {subtitle && (
              <Typography
                variant="subtitle"
                size="1"
                noPadding
                component="p"
                weight="700"
                className={clsx(
                  styles.subtitleDesktop,
                  variant === 'questions_broad' && styles.subtitleDesktopBroad
                )}
                color="black-new">
                {subtitle}
              </Typography>
            )}
            <div
              className={clsx(
                styles.questions,
                variant === 'questions_broad' && styles.questions_broad
              )}
              ref={acordionRef}>
              {questions.map((item: SanityKeyed<FaqItemV2>, index: number) => (
                <FaqQuestion
                  key={item._key}
                  {...item}
                  isOpen={item._key === selected}
                  onToggle={() =>
                    handleToggleQuestion({
                      itemKey: item._key,
                      itemHeadline: item.qHeadline,
                      toggledIndex: index
                    })
                  }
                />
              ))}
            </div>
          </Col>
        </Row>
      </Space>
    </Container>
  );
};

export default FaqBlockV2Markup;
